import { useState } from 'react';

import { TParsedProductInstance } from '@lib/core/products/types';
import { localeWidget } from '@lib/tools/locale/source/web/widget';
import { VH_VARIANTS } from '@lib/tools/shared/helpers/consts';

import { CaretDownIcon } from '@components/web/src/atoms/Icons/CaretDownIcon';
import { PriceRange } from '@components/web/src/atoms/PriceRange/PriceRange';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Image } from '@components/web/src/foundations/Image/Image';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS } from '@components/web/src/styles';
import * as S from '@components/web/src/templates/Modals/FindMeModal/Web/styles';
import { ModalWrapper } from '@components/web/src/templates/Modals/ModalWrapper/ModalWrapper';

interface Props {
  isOpen: boolean;
  hideModal: () => void;
  handleBtnClick?: () => void;
  parsedProductInstanceData: TParsedProductInstance;
}

export const FindMeModal = ({ isOpen, hideModal, handleBtnClick, parsedProductInstanceData }: Props) => {
  const { findMeText, viewMapText, viewProductText } = localeWidget.findMeModal;

  const [isShowProduct, setIsShowProduct] = useState(false);

  const toggleView = () => {
    handleBtnClick?.();
    setIsShowProduct(prev => !prev);
  };

  const {
    productImage,
    productName,
    productProducerName,
    productFormatName,
    productFormatSize,
    productDiscountPrice,
    productOriginalPrice,
    productDiscountPriceAndCurrency,
    productOriginalPriceAndCurrency,
    productPriceCurrencySymbol,
    productLocationDescription,
    isEnableLocationMapAddon,
    isPriceRangeAddon,
    productLocationMap,
    productCategory,
  } = parsedProductInstanceData ?? ({} as TParsedProductInstance);

  const isProductDiscountAvailable = !!productDiscountPrice && !!productOriginalPrice;
  const isProductLocationMapAvailable = isEnableLocationMapAddon && productLocationMap;

  return (
    <ModalWrapper noScroll isOpen={isOpen} onClose={hideModal}>
      <S.FindMeModalContainer
        isFullWidth
        direction="column"
        gap={16}
        padding={!isProductLocationMapAvailable ? '0 16px 32px 16px' : '0 16px 16px'}
      >
        <Flexbox align="center" direction="column" gap={0} justify="center" padding="12px 12px 0 12px">
          <S.CloseButtonCta handleClick={hideModal} position="right" />
          <Text fontType="grace" size="h5" text={findMeText} weight="semibold" />
        </Flexbox>

        <Flexbox isFullWidth direction="column" gap={8} padding="0">
          {!!productLocationDescription && (
            <S.TitleWrapper align="center" justify="center" padding="8px">
              <Text
                fontType="grace"
                size={productLocationDescription.length > 45 ? 'body2' : 'h6'}
                text={productLocationDescription}
                textAlign="center"
                weight="bold"
              />
            </S.TitleWrapper>
          )}

          <S.SlidingViewport>
            {isProductLocationMapAvailable && (
              <S.MapWrapper $isShowProduct={isShowProduct}>
                <S.ProductLocationMapWrapper isFullWidth align="center" justify="center">
                  <Image alt="location map" height="400px" src={productLocationMap} width="231px" />
                </S.ProductLocationMapWrapper>
              </S.MapWrapper>
            )}

            <S.ProductLayer
              $visible={isShowProduct || !isProductLocationMapAvailable}
              align="center"
              aria-hidden={!isShowProduct}
              direction="column"
              gap={28}
            >
              <Image alt="product image" height="231px" objectFit="contain" src={productImage} width="237px" />

              <S.ProductInfo isFullWidth direction="column" gap={24}>
                <Flexbox direction="column" gap={7}>
                  {!!productProducerName && (
                    <Text
                      color={COLORS.base.black}
                      linesLimit={1}
                      size="body1"
                      text={productProducerName}
                      weight="medium"
                    />
                  )}

                  {!!productName && (
                    <Text fontType="grace" linesLimit={2} size="h5" text={productName} weight="semibold" />
                  )}
                </Flexbox>

                <Flexbox align="center" justify="space-between">
                  {(productFormatName || productFormatSize) && (
                    <S.TextFormatWrapper>
                      <Text
                        size="body2"
                        text={`${productFormatName ? `${productFormatName}, ` : ''}${productFormatSize || ''}`}
                      />
                    </S.TextFormatWrapper>
                  )}
                  {isPriceRangeAddon ? (
                    <PriceRange
                      currencySymbolValue={productPriceCurrencySymbol}
                      originalPrice={productOriginalPrice}
                      productCategory={productCategory}
                    />
                  ) : (
                    <Flexbox gap={8} justify="flex-end">
                      {isProductDiscountAvailable && (
                        <Text
                          color={COLORS.gray[400]}
                          size="subtitle1"
                          text={productOriginalPriceAndCurrency}
                          textDecoration="line-through"
                        />
                      )}
                      {(isProductDiscountAvailable || !!productOriginalPrice) && (
                        <Text
                          color={COLORS.base.black}
                          size="subtitle1"
                          weight="semibold"
                          text={
                            isProductDiscountAvailable
                              ? productDiscountPriceAndCurrency
                              : productOriginalPriceAndCurrency
                          }
                        />
                      )}
                    </Flexbox>
                  )}
                </Flexbox>
              </S.ProductInfo>
            </S.ProductLayer>
          </S.SlidingViewport>

          {isProductLocationMapAvailable && (
            <S.ToggleBtn
              isFullWidth
              fontSize="body1"
              iconPosition="right"
              size="md"
              text={isShowProduct ? viewMapText : viewProductText}
              textDecoration="underline"
              textWeight="medium"
              variant={VH_VARIANTS.TRANSPARENT}
              icon={
                <S.ToggleArrowWrapper $directionUp={isShowProduct} align="center" justify="center">
                  <CaretDownIcon fillColor="var(--color-primary-700)" />
                </S.ToggleArrowWrapper>
              }
              onClick={toggleView}
            />
          )}
        </Flexbox>
      </S.FindMeModalContainer>
    </ModalWrapper>
  );
};
